import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { List, X, SignOut, UserCircle } from "@phosphor-icons/react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav_go = useNavigate();
  const [open, setOpen] = React.useState(false);

  const nav = [
    { to: "/", key: "home" },
    { to: "/marketplace/crypto", key: "crypto" },
    { to: "/marketplace/gold", key: "gold" },
    { to: "/marketplace/stocks", key: "stocks" },
    { to: "/marketplace/spacex", key: "spacex" },
    { to: "/usdf-reservation", key: "usdf" },
    { to: "/about", key: "about" },
    { to: "/faq", key: "faq" },
    { to: "/contact", key: "contact" },
  ];

  const doLogout = async () => {
    await logout();
    nav_go("/");
  };

  return (
    <>
      <div
        data-testid="inclusion-banner"
        className="w-full bg-[#141414] border-b border-[#E6C998]/20 text-[#E6C998] text-xs sm:text-sm py-2 text-center px-4"
      >
        <span className="overline mr-3 hidden sm:inline">{t("nav.inclusionOverline")}</span>
        {t("nav.inclusion")}
      </div>
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <div className="w-8 h-8 border border-[#E6C998] flex items-center justify-center">
              <span className="font-display text-[#E6C998] text-lg leading-none">M</span>
            </div>
            <div className="leading-tight">
              <div className="font-display text-white text-base tracking-tight">
                Madrefajona
              </div>
              <div className="overline text-[9px]">Reserved</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `text-sm transition-colors ${
                    isActive ? "text-[#E6C998]" : "text-neutral-300 hover:text-white"
                  }`
                }
                end={n.to === "/"}
              >
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="text-sm text-neutral-300 hover:text-white flex items-center gap-2"
                >
                  <UserCircle size={20} weight="duotone" className="text-[#E6C998]" />
                  {user.name?.split(" ")[0] || t("nav.dashboard")}
                </Link>
                <button
                  data-testid="nav-logout"
                  onClick={doLogout}
                  className="btn-outline-gold px-4 py-2 text-sm rounded-none flex items-center gap-2"
                >
                  <SignOut size={16} weight="duotone" /> {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="text-sm text-neutral-300 hover:text-white"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="btn-gold px-5 py-2 text-sm rounded-none"
                >
                  {t("nav.openAccount")}
                </Link>
              </>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              data-testid="nav-mobile-toggle"
              className="text-white p-2"
              onClick={() => setOpen((o) => !o)}
              aria-label="menu"
            >
              {open ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-[#0A0A0A] border-t border-white/10 px-5 py-6 flex flex-col gap-4">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${n.key}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#E6C998]" : "text-neutral-300"}`
                }
                end={n.to === "/"}
              >
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
            <div className="divider-gold my-2" />
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  className="text-[#E6C998]"
                  data-testid="nav-mobile-dashboard"
                >
                  {t("nav.dashboard")}
                </Link>
                <button
                  data-testid="nav-mobile-logout"
                  onClick={() => {
                    setOpen(false);
                    doLogout();
                  }}
                  className="btn-outline-gold px-4 py-2 text-sm rounded-none self-start"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-login"
                  className="text-neutral-200"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-register"
                  className="btn-gold px-5 py-2 text-sm rounded-none self-start"
                >
                  {t("nav.openAccount")}
                </Link>
              </>
            )}
          </div>
        )}
      </header>
    </>
  );
}
